/** Docs (UA): see modules/game/DOCS.md — keeps the field geometry in step with the viewport */

import { MutableRefObject, RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { computeLayout, FieldLayout } from '../lib/layout';

/** HUD height before it is measured — the reference one: 52 padding + 30 bar
 *  row (the gift icon, not the bar, sets it) + 8 gap + 32 timer pill. */
const DEFAULT_HUD_H = 122;

/** dpr над 2 не додає видимої якості, але множить площу канви вчетверо. */
const readDpr = (): number => Math.min(window.devicePixelRatio || 1, 2);

/** Півпікселя — межа, нижче якої зміна нічого не малює, а ре-рендер коштує.
 *  Без неї анімація згортання панелей Safari сипле десятками оновлень підряд. */
const nearlyEqual = (a: number, b: number): boolean => Math.abs(a - b) < 0.5;

const sameLayout = (a: FieldLayout, b: FieldLayout): boolean =>
    a.playable === b.playable &&
    a.dpr === b.dpr &&
    nearlyEqual(a.stageW, b.stageW) &&
    nearlyEqual(a.worldH * a.cssScale, b.worldH * b.cssScale) &&
    nearlyEqual(a.canopyH, b.canopyH) &&
    nearlyEqual(a.offsetX, b.offsetX);

export interface FieldLayoutHandle {
    /** Для DOM-шару: змінюється рідко, тягне ре-рендер. */
    layout: FieldLayout;
    /** Для кадрового циклу: читається щокадру без ре-рендера. */
    layoutRef: MutableRefObject<FieldLayout>;
}

/**
 * Тримає розкладку поля живою. Три джерела сигналу, бо жодне не покриває все:
 * ResizeObserver ловить будь-яку зміну коробки, `visualViewport` — згортання
 * адресного рядка iOS (window.resize там приходить не завжди), а
 * `orientationchange` спрацьовує раніше, ніж усядеться нова геометрія.
 * Усі три зливаються в один кадр, тож поворот коштує одного перерахунку.
 */
export const useFieldLayout = (
    boxRef: RefObject<HTMLElement | null>,
    hudEl: HTMLElement | null,
): FieldLayoutHandle => {
    const [layout, setLayout] = useState<FieldLayout>(() =>
        computeLayout(window.innerWidth, window.innerHeight, readDpr(), DEFAULT_HUD_H),
    );
    const layoutRef = useRef<FieldLayout>(layout);
    const hudHRef = useRef(DEFAULT_HUD_H);

    const measure = useCallback((): void => {
        const box = boxRef.current;
        if (!box) return;
        const rect = box.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        if (hudEl) {
            const hudH = hudEl.getBoundingClientRect().height;
            if (hudH > 0) hudHRef.current = hudH;
        }
        const next = computeLayout(rect.width, rect.height, readDpr(), hudHRef.current);
        if (sameLayout(layoutRef.current, next)) return;
        layoutRef.current = next;
        setLayout(next);
    }, [boxRef, hudEl]);

    useLayoutEffect(() => {
        let raf = 0;
        const schedule = (): void => {
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                measure();
            });
        };

        schedule();
        const observer = new ResizeObserver(schedule);
        if (boxRef.current) observer.observe(boxRef.current);
        if (hudEl) observer.observe(hudEl);
        window.addEventListener('resize', schedule);
        window.addEventListener('orientationchange', schedule);
        const viewport = window.visualViewport;
        viewport?.addEventListener('resize', schedule);
        viewport?.addEventListener('scroll', schedule);

        return () => {
            if (raf) window.cancelAnimationFrame(raf);
            observer.disconnect();
            window.removeEventListener('resize', schedule);
            window.removeEventListener('orientationchange', schedule);
            viewport?.removeEventListener('resize', schedule);
            viewport?.removeEventListener('scroll', schedule);
        };
    }, [boxRef, hudEl, measure]);

    // Поворот екрана: iOS повідомляє про нову орієнтацію ДО того, як браузер
    // перерахує коробку, тож один відкладений замір ловить фінальний розмір.
    useEffect(() => {
        let timer = 0;
        const onOrientation = (): void => {
            window.clearTimeout(timer);
            timer = window.setTimeout(measure, 250);
        };
        window.addEventListener('orientationchange', onOrientation);
        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('orientationchange', onOrientation);
        };
    }, [measure]);

    return { layout, layoutRef };
};
